



import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updatePlayerProfile, uploadVideo, deleteVideo } from '../../features/playerProfile/playerProfileSlice';
import { VideoPlayerModal } from '../../components/VideoPlayer/VideoPlayerModal';
import { VideoThumbnail } from '../../components/VideoPlayer/VideoThumbnail';
// SimpleDropdown removed - position dropdown no longer needed here

export const LevelUpProfileForm = ({ onBack }) => {
  const dispatch = useDispatch();
  const { profile, updateLoading, videoLoading } = useSelector((state) => state.playerProfile);

  const [formData, setFormData] = useState({
    email: '',
    title: '',
    description: '',
    position: 'Pitcher',
    tags: [],
    strengthInput: '',
    awards: [],
    xURL: '',
    instaURL: '',
    ncaaId: ''
  });

  const [strengthInput, setStrengthInput] = useState('');
  const [awardInput, setAwardInput] = useState('');
  const [uploadedVideos, setUploadedVideos] = useState([]);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [isPlayerOpen, setIsPlayerOpen] = useState(false);
  const [deletingVideoId, setDeletingVideoId] = useState(null);

  // Load profile data when component mounts
  useEffect(() => {
    if (profile) {
      setFormData({
        email: profile.email || '',
        title: profile.title || '',
        description: profile.description || '',
        position: profile.primaryPosition || 'Pitcher',
        tags: profile.strengths || [],
        strengthInput: '',
        awards: profile.awardsAchievements || [],
        xURL: profile.xURL || '',
        instaURL: profile.instaURL || '',
        ncaaId: profile.ncaaId || ''
      });
    }
  }, [profile]);

  // Sync uploaded videos with profile
  useEffect(() => {
    if (profile?.videos) {
      setUploadedVideos(profile.videos);
    } else {
      setUploadedVideos([]);
    }
  }, [profile?.videos]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddStrength = () => {
    if (strengthInput.trim()) {
      setFormData({ ...formData, tags: [...formData.tags, strengthInput.trim()] });
      setStrengthInput('');
    }
  };

  const handleStrengthKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddStrength();
    }
  };

  const handleRemoveStrength = (index) => {
    setFormData({ ...formData, tags: formData.tags.filter((_, i) => i !== index) });
  };

  const handleAddAward = () => {
    if (awardInput.trim()) {
      setFormData({ ...formData, awards: [...formData.awards, awardInput.trim()] });
      setAwardInput('');
    }
  };

  const handleAwardKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddAward();
    }
  };

  const handleRemoveAward = (index) => {
    setFormData({ ...formData, awards: formData.awards.filter((_, i) => i !== index) });
  };

  const handleVideoUpload = async (e) => {
    const file = e.target.files[0];
    if (file) {
      await dispatch(uploadVideo(file));
      // Video will be added to profile.videos via Redux, which will update uploadedVideos
    }
  };

  const handleVideoDelete = async (videoId) => {
    if (window.confirm('Are you sure you want to delete this video?')) {
      setDeletingVideoId(videoId);
      await dispatch(deleteVideo(videoId));
      setUploadedVideos((prev) => prev.filter((v) => v._id !== videoId));
      setDeletingVideoId(null);
    }
  };

  const handleVideoClick = (video) => {
    setSelectedVideo(video);
    setIsPlayerOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Update basic profile info with strengths and awards
    const profileData = {
      email: formData.email,
      title: formData.title,
      description: formData.description,
      primaryPosition: formData.position,
      strengths: formData.tags,
      awardsAchievements: formData.awards,
      xURL: formData.xURL,
      instaURL: formData.instaURL,
      ncaaId: formData.ncaaId
    };

    await dispatch(updatePlayerProfile(profileData));

    onBack();
  };

  return (
    <main className="flex-1 p-4 sm:p-6 overflow-y-auto border border-jp-line rounded-2xl m-6">
      <div className="max-w-3xl mx-auto">
        
        <form onSubmit={handleSubmit} className="space-y-4">

          {/* Email Input */}
          <div>
            <label className="block text-jp-ink text-sm font-medium mb-2 text-left">Enter your e-mail</label>
            <div className="relative">
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-lg bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent pr-10 transition-all"
                required
              />
            </div>
          </div>

          {/* Title */}
          <div>
            <label className="block text-jp-ink text-sm font-medium mb-2 text-left">Title</label>
            <div className="relative">
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                placeholder="Enter a title"
                maxLength={100}
                className="w-full px-4 py-3 rounded-lg bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition-all"
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-jp-muted text-xs">
                {formData.title.length}/100
              </span>
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="block text-jp-ink text-sm font-medium mb-2 text-left">Description</label>
            <div className="relative">
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                placeholder="Enter a description"
                maxLength={100}
                rows="3"
                className="w-full px-4 py-3 rounded-lg bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent resize-none transition-all"
              />
              <span className="absolute right-3 bottom-3 text-jp-muted text-xs">
                {formData.description.length}/100
              </span>
            </div>
          </div>

          {/* Social Links & NCAA ID Row */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            {/* X (Twitter) URL */}
            <div>
              <label className="block text-jp-ink text-sm font-medium mb-2 text-left">X (Twitter) URL</label>
              <input
                type="url"
                name="xURL"
                value={formData.xURL}
                onChange={handleInputChange}
                placeholder="https://x.com/username"
                className="w-full px-4 py-3 rounded-lg bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition-all"
              />
            </div>

            {/* Instagram URL */}
            <div>
              <label className="block text-jp-ink text-sm font-medium mb-2 text-left">Instagram URL</label>
              <input
                type="url"
                name="instaURL"
                value={formData.instaURL}
                onChange={handleInputChange}
                placeholder="https://instagram.com/username"
                className="w-full px-4 py-3 rounded-lg bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition-all"
              />
            </div>

            {/* NCAA ID */}
            <div>
              <label className="block text-jp-ink text-sm font-medium mb-2 text-left">NCAA ID</label>
              <input
                type="text"
                name="ncaaId"
                value={formData.ncaaId}
                onChange={handleInputChange}
                placeholder="Enter NCAA ID"
                className="w-full px-4 py-3 rounded-lg bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition-all"
              />
            </div>
          </div>

          {/* Add Strengths */}
          <div>
            <label className="block text-jp-ink text-sm font-medium mb-2 text-left">Add Strengths (Press Enter to add)</label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={strengthInput}
                onChange={(e) => setStrengthInput(e.target.value)}
                onKeyPress={handleStrengthKeyPress}
                placeholder="Enter a strength"
                className="flex-1 px-4 py-3 rounded-lg bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition-all"
              />
              <button
                type="button"
                onClick={handleAddStrength}
                className="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 rounded-lg font-medium transition-all"
              >
                Add
              </button>
            </div>
            {/* Display Strengths */}
            <label className="block text-jp-ink text-sm font-medium mb-2 text-left">Your Strengths</label>
            <div className="flex flex-wrap gap-2 p-3 rounded-lg bg-jp-section border border-jp-line min-h-[60px]">
              {formData.tags.map((tag, index) => (
                <span key={index} className="px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/40 text-emerald-700 dark:text-emerald-400 rounded-full text-sm font-medium flex items-center gap-2 whitespace-nowrap">
                  {tag}
                  <button
                    type="button"
                    onClick={() => handleRemoveStrength(index)}
                    className="hover:text-red-500 transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Video Upload Section */}
          <div>
            <label className="block text-jp-ink text-sm font-medium mb-2 text-left">Upload Videos</label>

            {/* Uploaded Videos Grid */}
            {uploadedVideos.length > 0 && (
              <div className="grid grid-cols-3 gap-3 mb-3">
                {uploadedVideos.map((video) => (
                  <div key={video._id} className="relative aspect-video bg-jp-section rounded-lg overflow-hidden">
                    <VideoThumbnail
                      videoSrc={video.videoUrl}
                      thumbnailUrl={video.thumbnailUrl}
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={() => handleVideoClick(video)}
                      className="absolute inset-0 flex items-center justify-center bg-black/40"
                    >
                      <div className="w-10 h-10 rounded-full bg-white/20 backdrop-blur flex items-center justify-center">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="white"><path d="M8 5v14l11-7z"/></svg>
                      </div>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleVideoDelete(video._id)}
                      disabled={deletingVideoId === video._id}
                      className="absolute top-1 right-1 w-6 h-6 rounded-full bg-red-500 hover:bg-red-600 flex items-center justify-center disabled:opacity-50"
                    >
                      {deletingVideoId === video._id ? (
                        <div className="w-3 h-3 border border-jp-line border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <svg width="10" height="10" viewBox="0 0 24 24" fill="white"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Upload Button */}
            <label className={`flex items-center justify-center px-8 py-3 bg-cyan-500 text-slate-900 rounded-lg font-bold text-sm uppercase tracking-wide transition-all shadow-lg ${
              videoLoading ? 'opacity-50 cursor-not-allowed' : 'hover:bg-cyan-400 cursor-pointer'
            }`}>
              <input
                type="file"
                accept="video/*"
                onChange={handleVideoUpload}
                className="hidden"
                disabled={videoLoading}
              />
              {videoLoading && (
                <div className="w-4 h-4 mr-2 border-2 border-jp-line border-t-transparent rounded-full animate-spin"></div>
              )}
              {uploadedVideos.length > 0 ? 'UPLOAD ANOTHER VIDEO' : 'UPLOAD VIDEO'}
            </label>
          </div>

          {/* Video Player Modal */}
          <VideoPlayerModal
            isOpen={isPlayerOpen}
            onClose={() => { setIsPlayerOpen(false); setSelectedVideo(null); }}
            video={selectedVideo}
            profile={profile}
            allVideos={uploadedVideos}
          />

          {/* Awards */}
          <div>
            <label className="block text-jp-ink text-sm font-medium mb-2 text-left">
              List Your Awards & Achievements <span className="text-jp-muted text-xs">Press Enter to add</span>
            </label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={awardInput}
                onChange={(e) => setAwardInput(e.target.value)}
                onKeyPress={handleAwardKeyPress}
                placeholder="Enter an award or achievement"
                className="flex-1 px-4 py-3 rounded-lg bg-jp-field border border-jp-line text-jp-ink placeholder-jp-muted/70 focus:outline-none focus:ring-2 focus:ring-jp-accent/40 focus:border-jp-accent transition-all"
              />
              <button
                type="button"
                onClick={handleAddAward}
                className="px-6 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 rounded-lg font-medium transition-all"
              >
                Add
              </button>
            </div>
            {/* Display Awards */}
            <div className="flex flex-wrap gap-2.5 p-3 rounded-lg bg-jp-section border border-jp-line min-h-[60px]">
              {formData.awards.map((award, index) => (
                <span key={index} className="px-4 py-2 bg-jp-accent/10 border border-jp-accent/40 text-jp-accent rounded-full text-sm font-semibold flex items-center gap-2.5">
                  {award}
                  <button
                    type="button"
                    onClick={() => handleRemoveAward(index)}
                    className="hover:text-red-500 transition-colors"
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
                    </svg>
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Submit Buttons */}
          <div className="flex items-center justify-center gap-3 pt-6">
            <button
              type="submit"
              disabled={updateLoading}
              className="px-12 py-3 bg-cyan-500 hover:bg-cyan-400 text-slate-900 rounded-lg font-bold text-base uppercase tracking-wide transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {updateLoading && (
                <div className="w-4 h-4 mr-2 border-2 border-jp-line border-t-transparent rounded-full animate-spin"></div>
              )}
              NEXT
            </button>
          </div>
        </form>
      </div>
    </main>
  );
};

export default LevelUpProfileForm;
